import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, ChevronRight, Clock3, Globe2, Info, Plus, Trash2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateStatusAPI } from "../../redux/action";
import "./AvailabilitySchedule.css";

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayOrder = [1, 2, 3, 4, 5, 6, 0];
const availableTimeZones = ["Asia/Kolkata", ...new Set([
  "UTC", ...(typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") :
    ["America/Los_Angeles", "America/New_York", "Europe/London", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney"]),
])].filter((zone, index, zones) => zone !== "Asia/Calcutta" && zones.indexOf(zone) === index);
const emptySlot = () => ({ days: [1, 2, 3, 4, 5], start: "06:00", end: "11:00" });
const displayTime = value => {
  if (!/^\d{2}:\d{2}$/.test(value || "")) return "Choose time";
  const [hour, minute] = value.split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
};
const describeSlot = slot => {
  const days = dayOrder.filter(day => slot.days.includes(day));
  const label = days.length === 7 ? "Every day" : days.join() === "1,2,3,4,5" ? "Mon–Fri" : days.map(day => weekdays[day]).join(", ") || "Choose days";
  return `${label} · ${displayTime(slot.start)}–${displayTime(slot.end)}${slot.end < slot.start ? " (next day)" : ""}`;
};

export default function AvailabilitySchedule({ menuId, categoryId, schedule, name }) {
  const dispatch = useDispatch();
  const menu = useSelector(state => state.user.menu);
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(Boolean(schedule));
  const [timeZone, setTimeZone] = useState(schedule?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [slots, setSlots] = useState(schedule?.slots || [emptySlot()]);
  const [validation, setValidation] = useState("");
  const dialogRef = useRef(null);
  const savingRef = useRef(false);
  const titleId = useId();
  const helpId = useId();

  // Keep drafts open during unrelated menu updates; close after our save returns.
  useEffect(() => {
    if (savingRef.current) { savingRef.current = false; setOpen(false); }
  }, [menu]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    dialog.querySelector("button")?.focus();
    return () => {
      if (typeof dialog.close === "function") dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  const close = () => { savingRef.current = false; setOpen(false); };
  const edit = () => {
    savingRef.current = false;
    setEnabled(Boolean(schedule));
    setTimeZone(schedule?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    setSlots(schedule?.slots?.map(slot => ({ ...slot, days: [...slot.days] })) || [emptySlot()]);
    setValidation("");
    setOpen(true);
  };
  const update = (index, values) => {
    setValidation("");
    setSlots(current => current.map((slot, i) => i === index ? { ...slot, ...values } : slot));
  };
  const save = event => {
    event.preventDefault();
    if (enabled) {
      try { new Intl.DateTimeFormat("en", { timeZone }).format(); }
      catch { setValidation("Choose a valid timezone, e.g. Asia/Kolkata."); return; }
      if (!slots.length || slots.some(slot => !slot.days.length || !slot.start || !slot.end || slot.start === slot.end)) {
        setValidation("Choose weekdays and different start/end times for each slot."); return;
      }
    }
    savingRef.current = true;
    dispatch(updateStatusAPI({ scheduleUpdate: true, data: { menuId, categoryId, schedule: enabled ? { timeZone, slots } : null } }));
  };

  return <div className={`availability-launcher${categoryId ? " availability-launcher--category" : ""}`}>
    <div className="availability-launcher__summary">
      <CalendarClock size={16} aria-hidden="true" />
      <span>{categoryId && <strong>Category schedule</strong>}
        {schedule?.slots?.length ? describeSlot(schedule.slots[0]) : "No weekly schedule"}
        {schedule?.slots?.length > 1 && <small> +{schedule.slots.length - 1} more</small>}
      </span>
    </div>
    <button className="availability-launcher__button" type="button" onClick={edit} aria-haspopup="dialog">
      {schedule ? "Edit schedule" : "Set schedule"}<ChevronRight size={16} aria-hidden="true" />
    </button>
    {open && createPortal(<dialog ref={dialogRef} className="availability-drawer" aria-labelledby={titleId} aria-describedby={helpId}
      onCancel={event => { event.preventDefault(); close(); }}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      }}>
      <form className="availability-drawer__form" onSubmit={save} noValidate>
        <header className="availability-drawer__header">
          <div className="availability-drawer__heading"><span className="availability-drawer__icon"><CalendarClock size={22} /></span>
            <div><p className="availability-drawer__eyebrow">{categoryId ? "CATEGORY AVAILABILITY" : "ITEM AVAILABILITY"}</p>
              <h2 id={titleId}>Availability schedule</h2></div>
          </div>
          <button type="button" className="availability-drawer__close" aria-label="Close schedule" onClick={close}><X size={20} /></button>
        </header>
        <div className="availability-drawer__body">
          <div className="availability-drawer__intro"><h3>{name || (categoryId ? "Selected category" : "Selected item")}</h3>
            <p id={helpId}>Set when customers can order. Items stay visible outside these hours.</p></div>
          <label className="availability-drawer__enable"><span><strong>Use weekly schedule</strong><small>Availability changes automatically.</small></span>
            <input type="checkbox" role="switch" aria-label="Use weekly schedule" checked={enabled} onChange={event => { setEnabled(event.target.checked); setValidation(""); }} />
          </label>
          {enabled ? <>
            <label className="availability-drawer__field"><span><Globe2 size={16} />Restaurant timezone</span>
              <select aria-label="Restaurant timezone" value={timeZone} onChange={event => { setTimeZone(event.target.value); setValidation(""); }}>
                {!availableTimeZones.includes(timeZone) && <option value={timeZone}>{timeZone}</option>}
                {availableTimeZones.map(zone => <option key={zone} value={zone}>{zone === "Asia/Kolkata" ? "India · IST (UTC+05:30)" : zone.replace(/_/g, " ")}</option>)}
              </select><small>All time slots follow this timezone.</small>
            </label>
            <div className="availability-drawer__section-title"><h3>Weekly hours</h3><span>{slots.length} {slots.length === 1 ? "time slot" : "time slots"}</span></div>
            {slots.map((slot, index) => <fieldset className="availability-slot" key={index}>
              <legend>Time slot {index + 1}</legend>
              <div className="availability-slot__toolbar"><span>Repeat on</span><button type="button" className="availability-slot__remove" aria-label={`Remove slot ${index + 1}`} onClick={() => { setSlots(slots.filter((_, i) => i !== index)); setValidation(""); }}><Trash2 size={15} />Remove slot</button></div>
              <div className="availability-slot__days">{dayOrder.map(day => <button key={day} type="button" aria-pressed={slot.days.includes(day)} aria-label={`${weekdays[day]}, time slot ${index + 1}`}
                onClick={() => update(index, { days: slot.days.includes(day) ? slot.days.filter(value => value !== day) : [...slot.days, day] })}>{weekdays[day]}</button>)}</div>
              <div className="availability-slot__times">
                <label className="availability-drawer__field">From<input aria-label={`From, time slot ${index + 1}`} type="time" value={slot.start} onChange={event => update(index, { start: event.target.value })} /></label>
                <label className="availability-drawer__field">To<input aria-label={`To, time slot ${index + 1}`} type="time" value={slot.end} onChange={event => update(index, { end: event.target.value })} /></label>
              </div>
              {slot.end < slot.start && <p className="availability-slot__overnight"><Clock3 size={14} />Ends the following day</p>}
            </fieldset>)}
            <button className="availability-drawer__add" type="button" disabled={slots.length >= 28} onClick={() => { setSlots([...slots, emptySlot()]); setValidation(""); }}><Plus size={17} />Add time slot</button>
            {slots.length > 0 && <section className="availability-drawer__preview" aria-label="Schedule summary"><h3><CalendarClock size={16} />Schedule summary</h3>
              <ul>{slots.map((slot, index) => <li key={index}>{describeSlot(slot)}</li>)}</ul>
            </section>}
          </> : <div className="availability-drawer__empty"><Clock3 size={25} /><h3>No time restrictions</h3><p>The manual availability toggle will control this {categoryId ? "category’s items" : "item"}.</p></div>}
          <p className="availability-drawer__note"><Info size={17} /><span>Manual unavailable always takes priority. When both item and category schedules are set, both must be open.</span></p>
        </div>
        <footer className="availability-drawer__footer">
          {validation && <p role="alert" className="availability-drawer__error">{validation}</p>}
          <div><button type="button" className="availability-drawer__cancel" onClick={close}>Cancel</button><button type="submit" className="availability-drawer__save">Save schedule</button></div>
        </footer>
      </form>
    </dialog>, document.body)}
  </div>;
}
